import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, User, Phone, MapPin, Zap, Calendar, Clock, DollarSign, Image as ImageIcon, CheckCircle, XCircle, AlertTriangle, PenLine, ArrowLeft, ClipboardCheck, Download, ChevronRight, X } from 'lucide-react';

const imageBase = 'https://app.sowashusa.com/uploads/';
const signatureFolder = 'signatures/';

const S = {
  page: { padding: '14px 20px', fontFamily: "'DM Sans',sans-serif" },
  card: { background: '#0D1120', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', overflow: 'hidden', marginBottom: '14px' },
  cardHead: { padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.55)', letterSpacing: '0.06em', textTransform: 'uppercase' },
  cardBody: { padding: '18px 20px' },
  fieldRow: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  fieldIcon: { marginTop: '2px' },
  fieldLabel: { fontSize: '10px', fontWeight: '700', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: '3px' },
  fieldValue: { fontSize: '14px', color: '#E2E8F0', fontWeight: '600' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  threeCol: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' },
  imgGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(100px,1fr))', gap: '8px' },
  imgThumb: { width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer', transition: 'opacity .2s' },
  emptyImg: { padding: '32px 20px', textAlign: 'center', color: 'rgba(255,255,255,0.22)', fontSize: '13px' },
  fsrItem: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '14px 16px' },
  fsrLabel: { fontSize: '10px', fontWeight: '700', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: '8px' },
  paymentBadge: (s) => {
    const map = { paid: { bg:'rgba(52,211,153,0.10)',border:'rgba(52,211,153,0.22)',color:'#34D399' }, pending: { bg:'rgba(251,191,36,0.10)',border:'rgba(251,191,36,0.22)',color:'#FBBF24' }, unpaid: { bg:'rgba(248,113,113,0.10)',border:'rgba(248,113,113,0.22)',color:'#F87171' } };
    const st = map[(s||'').toLowerCase()] || { bg:'rgba(255,255,255,0.07)',border:'rgba(255,255,255,0.12)',color:'#94A3B8' };
    return { display: 'inline-block', padding: '5px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', background: st.bg, border: `1px solid ${st.border}`, color: st.color };
  },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' },
  spinner: { width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.07)', borderTop: '3px solid #3B82F6', borderRadius: '50%', animation: 'ajd-spin 1s linear infinite', margin: '0 auto' },
};

function FsrFlag({ good, goodLabel='Good', badLabel }) {
  return good
    ? <div style={{ display:'flex', alignItems:'center', gap:'7px' }}><CheckCircle size={16} color="#34D399"/><span style={{ color:'#34D399', fontWeight:'700', fontSize:'13px' }}>{goodLabel}</span></div>
    : <div style={{ display:'flex', alignItems:'center', gap:'7px' }}><AlertTriangle size={16} color="#FBBF24"/><span style={{ color:'#FBBF24', fontWeight:'700', fontSize:'13px' }}>{badLabel}</span></div>;
}

const AdminJobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!id) return;
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/job-metrics/job-details/${id}`)
      .then(r => setJob(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [id]);

  const generatePDF = async () => {
    setDownloading(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: html2canvas } = await import('html2canvas');
      const el = document.getElementById('job-details-content');
      el.style.overflow = 'visible';
      const canvas = await html2canvas(el, { scale:2, useCORS:true, logging:false, backgroundColor:'#080C18' });
      el.style.overflow = '';
      const pdf = new jsPDF('p','mm','a4');
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      const r = Math.min(pw/canvas.width, ph/canvas.height);
      pdf.addImage(canvas.toDataURL('image/png'),'PNG',(pw-canvas.width*r)/2,10,canvas.width*r,canvas.height*r);
      pdf.save(`Job_Report_${job.job_id}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch { alert('Failed to generate PDF.'); }
    finally { setDownloading(false); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : 'N/A';
  const dur = (m) => { if(!m) return 'N/A'; const h=Math.floor(m/60),mn=m%60; return h>0?`${h}h ${mn}m`:`${mn}m`; };

  if (loading) return (
    <div style={{ ...S.page, display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh' }}>
      <div style={{ textAlign:'center' }}><div style={S.spinner}/><p style={{ color:'rgba(255,255,255,0.28)',marginTop:'14px',fontSize:'13px' }}>Loading job details…</p></div>
    </div>
  );

  if (!job) return (
    <div style={{ ...S.page, textAlign:'center', paddingTop:'80px' }}>
      <FileText size={48} color="rgba(255,255,255,0.15)" style={{ marginBottom:'16px' }}/>
      <p style={{ color:'rgba(255,255,255,0.40)',fontSize:'16px',fontWeight:'700' }}>Job not found</p>
      <button onClick={()=>navigate(-1)} style={{ marginTop:'16px', padding:'10px 20px', background:'linear-gradient(90deg,#3B82F6,#22D3A5)', color:'#fff', border:'none', borderRadius:'10px', fontWeight:'700', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>Go Back</button>
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap'); @keyframes ajd-spin{to{transform:rotate(360deg)}} .ajd-img:hover{opacity:.80}`}</style>

      {/* Header */}
      <div style={{ background:'#0D1120', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'14px', padding:'20px 24px 18px', marginBottom:'18px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', fontWeight:'700', letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(255,255,255,0.25)', marginBottom:'10px' }}>
          <span>Jobs</span><ChevronRight size={12} color="rgba(255,255,255,0.12)"/><span style={{ color:'rgba(34,211,165,0.55)' }}>Job #{job.job_id}</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:'12px', marginBottom:'14px' }}>
          <div>
            <div style={{ fontSize:'26px', fontWeight:'800', color:'rgba(255,255,255,0.92)', letterSpacing:'-0.03em', display:'flex', alignItems:'center', gap:'12px' }}>
              <FileText size={22} color="#3B82F6"/>Job Details #{job.job_id}
            </div>
            <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.28)', marginTop:'5px' }}>Complete job information and FSR report</div>
          </div>
          <div style={{ display:'flex', gap:'10px' }}>
            <button onClick={()=>navigate(-1)} style={{ padding:'9px 16px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', color:'rgba(255,255,255,0.45)', fontWeight:'700', fontSize:'12px', cursor:'pointer', display:'flex', alignItems:'center', gap:'7px', fontFamily:"'DM Sans',sans-serif" }}>
              <ArrowLeft size={14}/>Back
            </button>
            <button onClick={generatePDF} disabled={downloading} style={{ padding:'9px 16px', background:'linear-gradient(90deg,#3B82F6,#22D3A5)', color:'#fff', border:'none', borderRadius:'10px', fontWeight:'700', fontSize:'12px', cursor:downloading?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:'7px', opacity:downloading?0.6:1, fontFamily:"'DM Sans',sans-serif" }}>
              {downloading ? <span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,0.25)',borderTop:'2px solid white',borderRadius:'50%',animation:'ajd-spin .7s linear infinite',display:'inline-block'}}/> : <Download size={14}/>}
              {downloading?'Generating…':'Download PDF'}
            </button>
          </div>
        </div>
        <div style={{ height:'2px', borderRadius:'2px', background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }}/>
      </div>

      <div id="job-details-content">
        {/* Top 3 cards */}
        <div style={S.threeCol}>
          {/* Customer */}
          <div style={S.card}>
            <div style={S.cardHead}><User size={14} color="#3B82F6"/>Customer Info</div>
            <div style={S.cardBody}>
              {[
                { icon:<User size={13} color="rgba(255,255,255,0.25)"/>, label:'Name', val:job.customer?.name },
                { icon:<Phone size={13} color="rgba(255,255,255,0.25)"/>, label:'Phone', val:job.customer?.phone },
                { icon:<MapPin size={13} color="rgba(255,255,255,0.25)"/>, label:'City', val:job.customer?.city },
                { icon:<Zap size={13} color="#22D3A5"/>, label:'System Size', val:job.system_size_kw ? `${job.system_size_kw} kW` : null },
              ].map(({icon,label,val}) => (
                <div key={label} style={S.fieldRow}>
                  <div style={S.fieldIcon}>{icon}</div>
                  <div><div style={S.fieldLabel}>{label}</div><div style={S.fieldValue}>{val||'N/A'}</div></div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div style={S.card}>
            <div style={S.cardHead}><Clock size={14} color="#FBBF24"/>Timeline</div>
            <div style={S.cardBody}>
              {[
                { icon:<Calendar size={13} color="rgba(255,255,255,0.25)"/>, label:'Scheduled', val:fmt(job.scheduled_date) },
                { icon:<Clock size={13} color="#3B82F6"/>, label:'Started At', val:fmt(job.started_at) },
                { icon:<CheckCircle size={13} color="#34D399"/>, label:'Completed At', val:fmt(job.completed_at) },
                { icon:<Clock size={13} color="#A78BFA"/>, label:'Duration', val:dur(job.duration_minutes), badge:true },
              ].map(({icon,label,val,badge}) => (
                <div key={label} style={S.fieldRow}>
                  <div style={S.fieldIcon}>{icon}</div>
                  <div><div style={S.fieldLabel}>{label}</div>
                    {badge ? <span style={{ fontFamily:"'DM Mono',monospace", fontWeight:'700', fontSize:'14px', color:'#A78BFA', background:'rgba(167,139,250,0.10)', border:'1px solid rgba(167,139,250,0.20)', padding:'2px 10px', borderRadius:'6px' }}>{val}</span>
                           : <div style={S.fieldValue}>{val}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment */}
          <div style={S.card}>
            <div style={S.cardHead}><DollarSign size={14} color="#34D399"/>Payment</div>
            <div style={{ ...S.cardBody, display:'flex', alignItems:'center', justifyContent:'center', minHeight:'160px', flexDirection:'column', gap:'12px' }}>
              <DollarSign size={36} color="rgba(255,255,255,0.10)"/>
              <span style={S.paymentBadge(job.payment_status)}>{job.payment_status || 'Unknown'}</span>
            </div>
          </div>
        </div>

        {/* Images */}
        <div style={S.twoCol}>
          {[
            { label:'Before Images', key:'before_images' },
            { label:'After Images',  key:'after_images'  },
          ].map(({ label, key }) => (
            <div key={key} style={S.card}>
              <div style={S.cardHead}><ImageIcon size={14} color="#60A5FA"/>{label}</div>
              <div style={S.cardBody}>
                {job[key]?.length > 0
                  ? <div style={S.imgGrid}>{job[key].map((f,i) => <img key={i} className="ajd-img" src={`${imageBase}${f}`} alt="" style={S.imgThumb} onClick={()=>setSelectedImage(`${imageBase}${f}`)}/>)}</div>
                  : <div style={S.emptyImg}><ImageIcon size={28} color="rgba(255,255,255,0.10)" style={{ marginBottom:'8px' }}/><p style={{ margin:0 }}>No images available</p></div>}
              </div>
            </div>
          ))}
        </div>

        {/* FSR */}
        {job.fsr_fields && (
          <div style={S.card}>
            <div style={S.cardHead}><ClipboardCheck size={14} color="#22D3A5"/>Field Service Report</div>
            <div style={{ padding:'18px 20px', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'10px' }}>
              <div style={S.fsrItem}><div style={S.fsrLabel}>Cable Condition</div><FsrFlag good={job.fsr_fields.cable_condition==='Good'} goodLabel="Good" badLabel={job.fsr_fields.cable_condition||'Issue'}/></div>
              <div style={S.fsrItem}><div style={S.fsrLabel}>Panel Damage</div><FsrFlag good={job.fsr_fields.panel_damage==='None'} goodLabel="None" badLabel={job.fsr_fields.panel_damage||'Detected'}/></div>
              <div style={S.fsrItem}><div style={S.fsrLabel}>Inverter Alarm</div><FsrFlag good={job.fsr_fields.inverter_alarm==='No'} goodLabel="No Alarm" badLabel="Alarm Active"/></div>
              <div style={S.fsrItem}><div style={S.fsrLabel}>Bird Droppings</div><FsrFlag good={!job.fsr_fields.bird_dropping} goodLabel="Clean" badLabel="Yes"/></div>
              <div style={S.fsrItem}><div style={S.fsrLabel}>Rusting</div><FsrFlag good={!job.fsr_fields.rusting} goodLabel="None" badLabel="Detected"/></div>
            </div>
          </div>
        )}

        {/* Signature */}
        <div style={S.card}>
          <div style={S.cardHead}><PenLine size={14} color="#F472B6"/>Customer Signature</div>
          <div style={{ ...S.cardBody, textAlign:'center', padding:'24px' }}>
            {job.customer_signature
              ? <img src={job.customer_signature.startsWith('signature_') ? `${imageBase}${signatureFolder}${job.customer_signature}` : `${imageBase}${job.customer_signature}`} alt="Signature" style={{ maxWidth:'220px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.10)', padding:'14px', background:'rgba(255,255,255,0.02)' }}/>
              : <div style={S.emptyImg}><PenLine size={28} color="rgba(255,255,255,0.10)" style={{ marginBottom:'8px' }}/><p style={{ margin:0 }}>No signature available</p></div>}
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div style={S.overlay} onClick={()=>setSelectedImage(null)}>
          <div style={{ position:'relative', maxWidth:'90vw', maxHeight:'90vh' }}>
            <button onClick={()=>setSelectedImage(null)} style={{ position:'absolute', top:'-14px', right:'-14px', width:'32px', height:'32px', borderRadius:'50%', background:'rgba(255,255,255,0.10)', border:'1px solid rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><X size={16}/></button>
            <img src={selectedImage} alt="" style={{ maxWidth:'90vw', maxHeight:'90vh', objectFit:'contain', borderRadius:'12px' }}/>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobDetails;